"use client";

import "./business.css";
import "../job-board/JobBoard.css";
import { SideAds } from "../../components/Ads/Ads";
import FrequentServices from "./Components/FrequentServices";
import { useState, useEffect, useMemo } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Filterbtn from "./Components/Filterbtn";
import FilterPanel from "./Components/FilterPanel";
import businessApi from "../../services/businessApi";
import Pagination from "../../components/Pagination/Pagination";
import JobCardPlaceholder from "../../components/Placeholder/JobCardPlaceholder";
import useDebounce from "../../Hooks/useDebounce";
import strings from "../../config/App.json";
import "../blogs/[category]/Blogs.css";
import "../einbuergerungstest/ToolDetailsPage.css";
import "../einbuergerungstest/mocktest/bavaria/MockTest.css";
import "../einbuergerungstest/mocktest/bavaria/StateSelection.css";
import BusinessCard from "./Components/BusinessCard";
import BusinessSearchBar from "./Components/BusinessSearchBar";
import useFilterUrlSync from "@/Hooks/useFilterUrlSync";
import { toast } from "react-toastify";
import { MdShare } from "react-icons/md";
import { Business_sponsor_Ad, BusinessSideAds } from "@/components/Ads/businessAds";

export default function BusinessListingClient() {
    const [businesses, setBusinesses] = useState([]);
    const [sponsored, setSponsored] = useState([]);
    const [categories, setCategories] = useState([]);
    const [services, setServices] = useState([]);
    const [allServices, setAllServices] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState("germany");
    const [currentPage, setCurrentPage] = useState(1);
    const businessesPerPage = 10;
    const [totalPages, setTotalPages] = useState(1);
    const [totalBusinesses, setTotalBusinesses] = useState(0);
    const totalShownBusinesses = sponsored.length + businesses.length;
    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        categories: [],
        services: [],
        city: "All",
        search: "",
    });

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useDebounce(searchTerm, 400);
    const toggleFilters = () => setShowFilters(!showFilters);

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchMeta = async () => {
            try {
                const catResp = await businessApi.getCategories();
                const catData = catResp?.data ?? catResp;
                setCategories(Array.isArray(catData) ? catData : []);

                const svcResp = await businessApi.getServices();
                const svcData = svcResp?.data ?? svcResp;
                setAllServices(Array.isArray(svcData) ? svcData : []);
                setServices(Array.isArray(svcData) ? svcData : []);

                localStorage.setItem("businessServices", JSON.stringify(svcData));
            } catch (err) {
                console.error("Error fetching metadata:", err);
            }
        };

        fetchMeta();
    }, []);

    useEffect(() => {
        const fetchDependentServices = async () => {
            try {
                if (!filters.categories.length) {
                    setServices(allServices);
                    return;
                }

                const results = await Promise.all(
                    filters.categories.map((catId) =>
                        businessApi.getServicesByCategory(catId)
                    )
                );

                const merged = results.flatMap((r) => r?.data ?? r ?? []);

                const uniq = merged.filter(
                    (obj, index, arr) => index === arr.findIndex((t) => t.id === obj.id)
                );

                setServices(uniq);

                setFilters((prev) => ({
                    ...prev,
                    services: prev.services.filter((id) =>
                        uniq.some((s) => s.id === id)
                    ),
                }));
            } catch (err) {
                console.error("Error fetching dependent services:", err);
            }
        };

        if (allServices.length) fetchDependentServices();
    }, [filters.categories, allServices]);

    useEffect(() => {
        setFilters((prev) => ({ ...prev, search: debouncedSearch }));
        setCurrentPage(1);
    }, [debouncedSearch]);

    const handleTagClick = (tagName) => {
        const selectedCategory = categories.find((c) => c.name === tagName);
        if (!selectedCategory) return;

        const categoryId = selectedCategory.id;

        setFilters((prev) => {
            const exists = prev.categories.includes(categoryId);
            return {
                ...prev,
                categories: exists
                    ? prev.categories.filter((id) => id !== categoryId)
                    : [...prev.categories, categoryId],
            };
        });

        setSearchTerm("");
        setCurrentPage(1);
    };

    const handleSearchSubmit = (term) => {
        setFilters((prev) => ({ ...prev, search: term }));
        setCurrentPage(1);
    };

    const mergedBusinesses = [
        ...sponsored.map((b) => ({ ...b, isSponsored: true })),
        ...businesses.map((b) => ({ ...b, isSponsored: false })),
    ];

    const { isUrlApplied } = useFilterUrlSync({
        categories,
        services,
        setFilters,
        setCurrentPage,
        setSelectedCountry,
        setSearchTerm,
    });

    const businessQuery = useMemo(() => {
        const params = {
            page: currentPage,
            limit: businessesPerPage,
            country: selectedCountry || "germany",
        };

        const search = (filters.search || "").trim();
        if (search) params.search = search;
        if (filters.categories.length) params.categoryIds = filters.categories.join(",");
        if (filters.services.length) params.serviceIds = filters.services.join(",");
        if (filters.city && filters.city !== "All") params.city = filters.city;

        return params;
    }, [
        currentPage,
        selectedCountry,
        filters.search,
        filters.city,
        filters.categories,
        filters.services,
    ]);

    const businessQueryKey = useMemo(
        () => JSON.stringify(businessQuery),
        [businessQuery]
    );

    useEffect(() => {
        if (!isUrlApplied) return;

        let mounted = true;
        const fetchBusiness = async () => {
            try {
                setLoading(true);
                const response = await businessApi.getBusiness(businessQuery);
                const data = response?.data ?? response;

                if (!mounted) return;
                setSponsored(data.sponsored || []);
                setBusinesses(data.businesses || []);
                setTotalPages(data.pagination?.totalPages ?? 1);
                setTotalBusinesses(data.pagination?.total ?? 0);
            } catch (error) {
                if (!mounted) return;
                console.error("Error fetching businesses:", error);
                setBusinesses([]);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchBusiness();
        return () => {
            mounted = false;
        };
    }, [isUrlApplied, businessQueryKey]);

    const handleResetFilters = () => {
        setFilters({
            categories: [],
            services: [],
            city: "All",
            search: "",
        });

        setSelectedCountry("germany");
        setSearchTerm("");
        setCurrentPage(1);

        const cleanUrl = `${window.location.origin}${window.location.pathname}`;
        window.history.replaceState({}, "", cleanUrl);
    };

    const handleShareFilters = async () => {
        const params = new URLSearchParams();

        if (filters.categories.length)
            params.set("categories", filters.categories.join(","));
        if (filters.services.length)
            params.set("services", filters.services.join(","));
        if (filters.city && filters.city !== "All") params.set("city", filters.city);
        if (filters.search) params.set("search", filters.search);
        if (selectedCountry !== "germany") params.set("country", selectedCountry);
        if (currentPage > 1) params.set("page", currentPage);

        const shareUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;

        try {
            if (navigator.share) {
                await navigator.share({
                    title: "Business Listings",
                    text: "Check out these filtered businesses",
                    url: shareUrl,
                });
            } else {
                throw new Error("Web Share API not supported");
            }
        } catch (err) {
            try {
                await navigator.clipboard.writeText(shareUrl);
                toast.success("Link copied to clipboard");
            } catch (clipErr) {
                alert("Copy this link:\n\n" + shareUrl);
            }
        }
    };

    useEffect(() => {
        if (!isUrlApplied) return;
        const params = new URLSearchParams();

        if (filters.categories.length)
            params.set("categories", filters.categories.join(","));
        if (filters.services.length)
            params.set("services", filters.services.join(","));
        if (filters.city && filters.city !== "All") params.set("city", filters.city);
        if (filters.search) params.set("search", filters.search);
        if (selectedCountry !== "germany") params.set("country", selectedCountry);
        if (currentPage > 1) params.set("page", currentPage);

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", newUrl);
    }, [filters, selectedCountry, currentPage]);

    return (
        <>
            <Header />
            <div className="job-content" style={{ position: 'relative' }}>
                <div className="background-ellipses">
                    <div className="ellipse ellipse-1"></div>
                    <div className="ellipse ellipse-2"></div>
                </div>

                <div className="page-container">
                    <div className='job-header-container'>
                        <div className='job-header-texts'>
                            <h1 className="Job_title">
                                Search across Businesses in <span className="text-style-1">Munich & India</span>
                            </h1>
                            <p className="Job_des">Find what service you are looking for...</p>
                        </div>

                        <button
                            className="apply-btn"
                            id='ListBusinessBtn'
                            onClick={() => window.location.href = strings.ListBusinessPage}
                        >
                            <img src='/images/Job/add.svg' alt="Add" className="addIcon" />
                            <span className="locationIcon">List Your Business</span>
                        </button>
                    </div>

                    <div className="searchFlex" id='searchFlex'>
                        <div className="searchBar" id='searchBar'>
                            <BusinessSearchBar
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                categories={categories}
                                services={services}
                                onSearchSubmit={handleSearchSubmit}
                            />
                        </div>
                    </div>

                    {/* <div className='job-inf-container'>
                        <span className='job-inf' onClick={openModal}>
                            <IoIosInformationCircle size={20} color='rgba(141, 141, 153, 1)' />
                            Important information on the business listings
                        </span>
                    </div> */}

                    <div className="business-filter">
                        <div className="business-filter-section">

                            <div
                                className={`country_filterBtn ${selectedCountry === "germany" ? "active" : ""}`}
                                onClick={() => { setSelectedCountry("germany"); setCurrentPage(1); }}
                                id="munichBtn"
                            >
                                <img src="/images/Business/Germany-Flag.svg" alt="Munich" />
                                Businesses in Munich
                            </div>

                            <div
                                className={`country_filterBtn ${selectedCountry === "india" ? "active" : ""}`}
                                onClick={() => { setSelectedCountry("india"); setCurrentPage(1); }}
                                id='indiaBtn'
                            >
                                <img src="/images/Business/India-Flag.svg" alt="India" />
                                Businesses in India
                            </div>
                        </div>
                        <div className="business-filter-section" id='filter_btn_section'>
                            <button
                                className="filter_btn"
                                id="filter_reset_btn"
                                onClick={handleResetFilters}
                            >
                                Reset Filter
                            </button>

                            <button
                                className="filter_btn"
                                id="filter_share_btn"
                                onClick={handleShareFilters}
                            >
                                <span className="shareIcon">
                                    <MdShare />
                                    Share
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="filter-container">
                        {filters.search ? (
                            <div className="Search-results" id="search_results">
                                Showing results for <span className="text-style-2">{filters.search}</span> <br />
                                <span className="Search-results-count">
                                    {loading ? "..." : totalShownBusinesses} results found
                                </span>
                            </div>
                        ) : (
                            <FrequentServices
                                categories={categories}
                                onServiceClick={handleTagClick}
                                filters={filters}
                            />
                        )}

                        <Filterbtn toggleFilters={toggleFilters} showFilters={showFilters} />
                    </div>

                    {showFilters && (
                        <FilterPanel
                            filters={filters}
                            setFilters={setFilters}
                            categories={categories}
                            services={services}
                            totalBusinesses={totalBusinesses}
                            selectedCountry={selectedCountry}
                        />
                    )}

                    <div className="content-container_wrapper" id="content-container_wrapper">
                        <div className="content-container">

                            {loading ? (
                                Array.from({ length: 3 }).map((_, i) => <JobCardPlaceholder key={i} />)
                            ) : (
                                <>
                                    <Business_sponsor_Ad />
                                    {mergedBusinesses.length > 0 ? (
                                        mergedBusinesses.map((b, index) => (
                                            <BusinessCard
                                                key={`${b.source_record_id}-${b.isSponsored ? "s" : "n"}`}
                                                company={b.business_name}
                                                category={b.categories}
                                                description={b.services}
                                                location={[
                                                    b.address1,
                                                    b.address2,
                                                    b.city,
                                                    b.state,
                                                    b.postal_code,
                                                    b.country,
                                                ]
                                                    .filter(Boolean)
                                                    .join(", ")
                                                }
                                                logo={b.logo}
                                                website_url={b.website_url}
                                                contact={b.contact_no}
                                                mail={b.sender_email}
                                                isSponsored={b.isSponsored}
                                                source_record_id={b.source_record_id}
                                            />

                                        ))
                                    ) : (
                                        <p>No businesses found.</p>
                                    )}
                                </>
                            )}

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                scrollToId="search-wrapper"
                            />
                        </div>
                        <div className="Business-advertisement">
                            <BusinessSideAds />
                            <br />
                            <SideAds />
                        </div>
                    </div>
                </div>
            </div>
            {/* <BusinessInfoModel isOpen={modalIsOpen} onRequestClose={closeModal} /> */}
            <Footer />
        </>
    );
}
